import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

// Stages a card into the edit grid without a reload. The POST returns a turbo_stream that appends into
// #report-grid-cards, so unsaved edits are preserved. Uses request.js (not button_to) because admin has
// Turbo Drive off, so a plain form submit wouldn't be processed as a stream.
export default class extends Controller {
  static values = { url: String, showUrlTemplate: String, showLiveUrlTemplate: String };
  static targets = ["selectedChart", "addButton"];

  async addSection(event) {
    await this.stage({ variant: event.params.variant });
  }

  // The preview frame reloads carrying the previewed chart's id; remember it and enable the footer
  // button. Mo and live previews stage through different params, so remember which kind it was too.
  selectedChartTargetConnected(element) {
    this.selectedChartId = element.dataset.chartId;
    this.selectedLiveChartId = element.dataset.liveChartId;
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = false;
    }
  }

  // Fired when a preview link is clicked. Turbo keeps the old preview mounted until the new one loads,
  // so drop the stale selection and disable the button until selectedChartTargetConnected re-enables it.
  // Otherwise a quick Add chart during a slow preview load would act on the previously previewed chart.
  clearSelection() {
    this.selectedChartId = null;
    this.selectedLiveChartId = null;
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = true;
    }
  }

  // :edit stages the previewed chart into the grid in place; :show has no live grid, so it navigates to
  // the edit page with the chart staged (showUrlTemplate carries a CHART_ID placeholder to substitute).
  // AWAITED, and the dialog closes only afterwards: a parallel close let a quick Save submit before the
  // Turbo Stream appended the staged card, reporting success while dropping the chart.
  // :show has no grid, so it navigates instead of staging.
  async addSelectedChart() {
    const live = !!this.selectedLiveChartId;
    if (!live && !this.selectedChartId) return;

    const template = live ? this.showLiveUrlTemplateValue : this.showUrlTemplateValue;
    if (template) {
      const token = live ? "LIVE_CHART_ID" : "CHART_ID";
      window.location.assign(template.replace(token, live ? this.selectedLiveChartId : this.selectedChartId));
      return;
    }

    await this.stage(live ? { report_chart_id: this.selectedLiveChartId }
                          : { massimo_chart_id: this.selectedChartId });
    this.closeDialog();
  }

  closeDialog() {
    this.element.querySelector("[data-ui--dialog-target='closeButton']")?.click();
  }

  async stage(body) {
    await post(this.urlValue, { body, responseKind: "turbo-stream" });
    this.dispatch("added");
  }
}
